import React, { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const ProductDetail = ({
  product,
  products,
  isOpen,
  onClose,
  onProductSwitch,
}) => {
  const [activeTab, setActiveTab] = useState("specification");
  const [currentProduct, setCurrentProduct] = useState(product);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const modalContentRef = useRef(null);

  useEffect(() => {
    if (product) {
      setCurrentProduct(product);
      // Filter related products (products from the same category excluding current one)
      if (products && products.length > 0) {
        setRelatedProducts(
          products.filter((p) => p.id !== product.id).slice(0, 4)
        );
      }
    }
  }, [product, products]);

  const modalVariants = {
    initial: { opacity: 0, y: 100 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        type: "spring",
        damping: 25,
      },
    },
    exit: {
      opacity: 0,
      y: 100,
      transition: { duration: 0.3 },
    },
  };

  const contentVariants = {
    initial: { opacity: 0, y: -100 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: 100,
      transition: { duration: 0.3, ease: "easeIn" },
    },
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleProductChange = (newProduct) => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    setTimeout(() => {
      setCurrentProduct(newProduct);
      onProductSwitch(newProduct);
      setIsTransitioning(false);
      setActiveTab("specification");

      if (modalContentRef.current) {
        const scrollContainer =
          modalContentRef.current.querySelector(".overflow-y-auto");
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    }, 50);
  };

  if (!currentProduct) return null;

  const fallbackImageUrl =
    "https://via.placeholder.com/800x600?text=Product+Image";
  const productImage = currentProduct.image_path
    ? `${process.env.REACT_APP_API_URL}${currentProduct.image_path}`
    : fallbackImageUrl;

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
          onClick={handleOverlayClick}
        >
          <motion.div
            ref={modalContentRef}
            variants={modalVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="relative w-[1000px] h-[calc(100vh-0px)] my-5"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 bg-black/40 p-2 rounded-full hover:bg-black/90 transition-all duration-300"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            <div className="h-full overflow-y-auto bg-white scrollbar-hide">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentProduct.id}
                  variants={contentVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
                    {/* Product Image */}
                    <div>
                      <img
                        src={productImage}
                        alt={currentProduct.title}
                        className="w-full rounded-lg object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = fallbackImageUrl;
                        }}
                      />
                    </div>

                    {/* Product Info */}
                    <div>
                      <h1 className="text-3xl font-bold text-gray-800 mb-2">
                        {currentProduct.title}
                      </h1>
                      <p className="text-gray-700 mb-6">
                        {currentProduct.category?.name ||
                          "Premium Battery Material"}
                      </p>

                      <div className="mb-8">
                        <h2 className="text-xl font-semibold mb-4">
                          Product Features:
                        </h2>
                        {/* Di dalam ProductDetail */}
                        {currentProduct.features &&
                        currentProduct.features.length > 0 ? (
                          <ul className="space-y-2">
                            {currentProduct.features.map((feature, index) => (
                              <li key={index} className="flex items-center">
                                <span className="mr-2">•</span>
                                {/* Cek tipe feature */}
                                {typeof feature === "string"
                                  ? feature
                                  : feature.feature_name}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-500 italic">
                            No features available
                          </p>
                        )}
                      </div>

                      {/* <div>
                        <button
                          className="inline-block bg-blue-600 text-white font-medium py-2 px-6 rounded-md hover:bg-blue-700 transition duration-300"
                          onClick={() => (window.location.href = "/contact")}
                        >
                          Request Quote
                        </button>
                      </div> */}
                    </div>
                  </div>

                  {/* Tabs Navigation */}
                  <div className="px-8">
                    <div className="border-b border-gray-200">
                      <nav className="flex -mb-px">
                        <button
                          onClick={() => setActiveTab("specification")}
                          className={`py-4 px-6 font-medium text-sm border-b-2 ${
                            activeTab === "specification"
                              ? "border-blue-600 text-blue-600"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                          }`}
                        >
                          Specification
                        </button>
                        <button
                          onClick={() => setActiveTab("quality")}
                          className={`py-4 px-6 font-medium text-sm border-b-2 ${
                            activeTab === "quality"
                              ? "border-blue-600 text-blue-600"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                          }`}
                        >
                          Quality & Certification
                        </button>
                        <button
                          onClick={() => setActiveTab("testing")}
                          className={`py-4 px-6 font-medium text-sm border-b-2 ${
                            activeTab === "testing"
                              ? "border-blue-600 text-blue-600"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                          }`}
                        >
                          Testing & Validation
                        </button>
                      </nav>
                    </div>

                    {/* Tab Content */}
                    <motion.div
                      initial="initial"
                      animate="animate"
                      variants={fadeInUp}
                      className="my-6"
                    >
                      {activeTab === "specification" && (
                        <div className="overflow-x-auto">
                          {currentProduct.specifications &&
                          currentProduct.specifications.length > 0 ? (
                            <table className="min-w-full divide-y divide-gray-200">
                              <tbody className="bg-white divide-y divide-gray-200">
                                {currentProduct.specifications.map(
                                  (spec, index) => (
                                    <tr key={index}>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50 w-1/3">
                                        {spec.spec_name}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {spec.spec_value}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          ) : (
                            <div className="bg-white p-6 rounded-lg">
                              <p className="text-gray-500 italic">
                                No specifications available
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {activeTab === "quality" && (
                        <div className="bg-white p-6 rounded-lg">
                          <h2 className="text-xl font-bold mb-4">
                            Quality Assurance
                          </h2>
                          <p className="mb-4">
                            Our products undergo rigorous quality control
                            processes to ensure they meet the highest industry
                            standards.
                          </p>
                          <h3 className="text-lg font-semibold mb-2">
                            Certifications:
                          </h3>
                          {currentProduct.certifications &&
                          currentProduct.certifications.length > 0 ? (
                            <ul className="list-disc pl-5 space-y-2">
                              {currentProduct.certifications.map(
                                (cert, index) => (
                                  <li key={index}>{cert.certification_name}</li>
                                )
                              )}
                            </ul>
                          ) : (
                            <p className="text-gray-500 italic">
                              No certifications available
                            </p>
                          )}
                        </div>
                      )}

                      {activeTab === "testing" && (
                        <div className="bg-white p-6 rounded-lg">
                          <h2 className="text-xl font-bold mb-4">
                            Testing Methodology
                          </h2>
                          <p className="mb-4">
                            Each product undergoes comprehensive testing to
                            validate performance, durability, and safety
                            characteristics.
                          </p>

                          <h3 className="text-lg font-semibold mb-2">
                            Testing Capabilities:
                          </h3>
                          {currentProduct.testingCapabilities &&
                          currentProduct.testingCapabilities.length > 0 ? (
                            <ul className="list-disc pl-5 space-y-2 mb-6">
                              {currentProduct.testingCapabilities.map(
                                (capability, index) => (
                                  <li key={index}>
                                    {capability.capability_name}
                                  </li>
                                )
                              )}
                            </ul>
                          ) : (
                            <p className="text-gray-500 italic mb-6">
                              No testing capabilities available
                            </p>
                          )}

                          <h3 className="text-lg font-semibold mb-2">
                            Validation Methods:
                          </h3>
                          {currentProduct.validationMethods &&
                          currentProduct.validationMethods.length > 0 ? (
                            <ul className="list-disc pl-5 space-y-2">
                              {currentProduct.validationMethods.map(
                                (method, index) => (
                                  <li key={index}>{method.method_name}</li>
                                )
                              )}
                            </ul>
                          ) : (
                            <p className="text-gray-500 italic">
                              No validation methods available
                            </p>
                          )}
                        </div>
                      )}
                    </motion.div>
                  </div>

                  {/* Related Products Section */}
                  {relatedProducts.length > 0 && (
                    <motion.div
                      initial="initial"
                      animate="animate"
                      variants={fadeInUp}
                      className="px-8 py-10 bg-gray-50"
                    >
                      <h2 className="text-2xl font-bold mb-6">
                        Related Products
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {relatedProducts.map((relatedProduct) => (
                          <RelatedProductCard
                            key={relatedProduct.id}
                            product={relatedProduct}
                            onClick={() => handleProductChange(relatedProduct)}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* Call to Action Section */}
                  <motion.div
                    initial="initial"
                    animate="animate"
                    variants={fadeInUp}
                    className="bg-primary py-12 text-center"
                  >
                    <div className="max-w-3xl mx-auto px-4">
                      <h2 className="text-3xl font-bold text-white mb-4">
                        Interested in Our Products?
                      </h2>
                      <p className="text-white/90 mb-8">
                        Contact us to learn more about our products and
                        materials
                      </p>
                      <button
                        className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                        onClick={() => (window.location.href = "/contact")}
                      >
                        Get in Touch
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// Related Product Card Component
const RelatedProductCard = ({ product, onClick }) => {
  const fallbackImage =
    "https://via.placeholder.com/400x300?text=Product+Image";
  const productImage = product?.image_path
    ? `${process.env.REACT_APP_API_URL}${product.image_path}`
    : fallbackImage;

  return (
    <div
      className="bg-white rounded-lg shadow-md overflow-hidden group h-full cursor-pointer"
      onClick={onClick}
    >
      <div className="h-48 overflow-hidden">
        <img
          src={productImage}
          alt={product.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = fallbackImage;
          }}
        />
      </div>
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">
          {product.title}
        </h3>
        <p className="text-sm text-gray-600 line-clamp-2">
          {product.description || "No description available"}
        </p>
      </div>
    </div>
  );
};

export default ProductDetail;
